import React, { useState, useCallback } from 'react'
import config from '../../config.js'
import PartCard, { partsGridStyle } from './PartCard'

const NESTER = config.NESTER_API_URL

const GROUP_COLORS = ['#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706', '#0891b2']

// Surface FastAPI 400/422 {"detail":...} bodies as readable operator text
// (e.g. the laser_tech_specs "no spec exists for X / Y" message from save).
async function readErrorDetail(res) {
  const text = await res.text()
  try {
    const j = JSON.parse(text)
    return j.detail || j.message || text
  } catch { return text }
}

export default function MaterialGroupsPanel({
  projectId,
  groups,
  onGroupsChange,
  materialTypes,
  thicknesses,
  laserSpecs,
  surfaceFinishes,
  parts = [],
  fillResolutions,
  onUpdatePartField,
  onUpdatePartQuantity,
  onDeletePart,
  onError,
}) {
  const [expandedId, setExpandedId] = useState(groups.length > 0 ? groups[0].group_id : null)
  const [adding, setAdding] = useState(false)

  async function addGroup() {
    setAdding(true)
    try {
      const res = await fetch(`${NESTER}/projects/${projectId}/material_groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!res.ok) throw new Error(await readErrorDetail(res))
      const created = await res.json()
      onGroupsChange([...groups, created])
      setExpandedId(created.group_id)
    } catch (e) {
      if (onError) onError(e.message)
      else console.error('Failed to create group:', e)
    }
    setAdding(false)
  }

  async function deleteGroup(groupId) {
    if (!window.confirm('Delete this material group? Parts will be unassigned.')) return
    try {
      await fetch(`${NESTER}/material_groups/${groupId}`, { method: 'DELETE' })
      onGroupsChange(groups.filter(g => g.group_id !== groupId))
    } catch (e) { console.error('Failed to delete group:', e) }
  }

  async function saveGroup(groupId, data) {
    try {
      const res = await fetch(`${NESTER}/material_groups/${groupId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error(await readErrorDetail(res))
      const updated = await res.json()
      onGroupsChange(groups.map(g => g.group_id === groupId ? updated : g))
    } catch (e) {
      if (onError) onError(e.message)
      else console.error('Failed to save group:', e)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
          {groups.length} group{groups.length !== 1 ? 's' : ''} — assign parts to groups below, each nests on its own sheet type
        </div>
        <button className="btn btn-sm btn-outline-primary" onClick={addGroup} disabled={adding}
          style={{ fontWeight: 600 }}>
          + Add Group
        </button>
      </div>
      {groups.map((group, idx) => (
        <GroupCard
          key={group.group_id}
          group={group}
          color={GROUP_COLORS[idx % GROUP_COLORS.length]}
          expanded={expandedId === group.group_id}
          onToggle={() => setExpandedId(expandedId === group.group_id ? null : group.group_id)}
          onSave={data => saveGroup(group.group_id, data)}
          onDelete={() => deleteGroup(group.group_id)}
          materialTypes={materialTypes}
          thicknesses={thicknesses}
          laserSpecs={laserSpecs}
          surfaceFinishes={surfaceFinishes}
          parts={parts.filter(p => p.material_group_id === group.group_id)}
          allGroups={groups}
          fillResolutions={fillResolutions}
          onUpdatePartField={onUpdatePartField}
          onUpdatePartQuantity={onUpdatePartQuantity}
          onDeletePart={onDeletePart}
        />
      ))}
    </div>
  )
}

export { GROUP_COLORS }


function GroupCard({ group, color, expanded, onToggle, onSave, onDelete, materialTypes, thicknesses, laserSpecs, surfaceFinishes, parts = [], allGroups = [], fillResolutions, onUpdatePartField, onUpdatePartQuantity, onDeletePart }) {
  const [materialType, setMaterialType] = useState(group.material_type || '')
  const [thicknessDisplay, setThicknessDisplay] = useState(group.thickness_display || '')
  const [thicknessIn, setThicknessIn] = useState(group.thickness_in?.toString() || '')
  const [sheetWidth, setSheetWidth] = useState(group.sheet_width?.toString() || '48')
  const [sheetLength, setSheetLength] = useState(group.sheet_length?.toString() || '96')
  const [feedrateIpm, setFeedrateIpm] = useState(group.feedrate_ipm?.toString() || '')
  const [pierceTimeSec, setPierceTimeSec] = useState(group.pierce_time_sec?.toString() || '')
  const [gasType, setGasType] = useState(group.gas_type || '')
  const [customerSupplied, setCustomerSupplied] = useState(group.customer_supplied_material || false)
  const [costPerSheet, setCostPerSheet] = useState(group.cost_per_sheet?.toString() || '')
  const [costPerLb, setCostPerLb] = useState(group.cost_per_lb?.toString() || '')
  const [gasFlowCfh, setGasFlowCfh] = useState(group.gas_flow_cfh?.toString() || '')
  const [gasCostPerCf, setGasCostPerCf] = useState(group.gas_cost_per_cf?.toString() || '')
  const [consumableRate, setConsumableRate] = useState(group.consumable_rate_per_hour?.toString() || '')
  const [partGap, setPartGap] = useState(group.part_gap?.toString() || '')
  const [edgeMargin, setEdgeMargin] = useState(group.edge_margin?.toString() || '')
  const [surfaceFinish, setSurfaceFinish] = useState(group.surface_finish || '')
  const [nozzleCleanPierces, setNozzleCleanPierces] = useState(group.nozzle_clean_pierces?.toString() || '')
  const [nozzleCleanSec, setNozzleCleanSec] = useState(group.nozzle_clean_sec?.toString() || '')
  const [dirty, setDirty] = useState(false)

  const filteredThicknesses = materialType
    ? thicknesses.filter(t => t.material_type?.toLowerCase() === materialType.toLowerCase())
    : thicknesses

  function markDirty() { setDirty(true) }

  const handleThicknessChange = useCallback((display, inches) => {
    setThicknessDisplay(display)
    setThicknessIn(inches)
    if (inches) {
      setPartGap(inches)
      setEdgeMargin(inches)
    }
    setDirty(true)

    if (materialType && display) {
      const spec = laserSpecs.find(s =>
        s.material_type?.toLowerCase() === materialType.toLowerCase() &&
        s.display_thickness === display
      )
      if (spec) {
        setFeedrateIpm(spec.feedrate_ipm?.toString() || '')
        setPierceTimeSec(spec.pierce_time_sec?.toString() || '')
        setGasType(spec.gas_type || '')
        if (spec.gas_flow_cfh) setGasFlowCfh(spec.gas_flow_cfh.toString())
      }

      const params = new URLSearchParams({ material_type: materialType, thickness_display: display })
      if (sheetWidth) params.append('sheet_width', sheetWidth)
      if (sheetLength) params.append('sheet_length', sheetLength)

      fetch(`${NESTER}/cost_lookup?${params}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return
          if (data.gas_flow_cfh) setGasFlowCfh(data.gas_flow_cfh.toString())
          if (data.gas_cost_per_cf) setGasCostPerCf(data.gas_cost_per_cf.toString())
          if (data.consumable_rate_per_hour) setConsumableRate(data.consumable_rate_per_hour.toString())
          if (data.cost_per_sheet) setCostPerSheet(data.cost_per_sheet.toString())
          if (data.cost_per_lb) setCostPerLb(data.cost_per_lb.toString())
        })
        .catch(() => {})
    }
  }, [materialType, laserSpecs, sheetWidth, sheetLength])

  function handleSave() {
    onSave({
      material_type: materialType || null,
      thickness_display: thicknessDisplay || null,
      thickness_in: thicknessIn ? parseFloat(thicknessIn) : null,
      sheet_width: sheetWidth ? parseFloat(sheetWidth) : null,
      sheet_length: sheetLength ? parseFloat(sheetLength) : null,
      feedrate_ipm: feedrateIpm ? parseFloat(feedrateIpm) : null,
      pierce_time_sec: pierceTimeSec ? parseFloat(pierceTimeSec) : null,
      gas_type: gasType || null,
      nozzle_clean_pierces: nozzleCleanPierces ? parseInt(nozzleCleanPierces) : null,
      nozzle_clean_sec: nozzleCleanSec ? parseFloat(nozzleCleanSec) : null,
      customer_supplied_material: customerSupplied,
      cost_per_sheet: customerSupplied ? null : (costPerSheet ? parseFloat(costPerSheet) : null),
      cost_per_lb: customerSupplied ? null : (costPerLb ? parseFloat(costPerLb) : null),
      gas_flow_cfh: gasFlowCfh ? parseFloat(gasFlowCfh) : null,
      gas_cost_per_cf: gasCostPerCf ? parseFloat(gasCostPerCf) : null,
      consumable_rate_per_hour: consumableRate ? parseFloat(consumableRate) : null,
      part_gap: partGap ? parseFloat(partGap) : null,
      edge_margin: edgeMargin ? parseFloat(edgeMargin) : null,
      surface_finish: surfaceFinish || null,
    })
    setDirty(false)
  }

  const label = group.group_name || `Group ${group.group_id}`

  return (
    <div style={{
      border: `1px solid ${expanded ? color : '#e5e7eb'}`,
      borderRadius: '8px', marginBottom: '0.5rem',
      borderLeft: `4px solid ${color}`,
    }}>
      <div onClick={onToggle} style={{
        padding: '0.5rem 0.75rem', cursor: 'pointer',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: expanded ? '#f8fafc' : 'transparent',
        borderRadius: expanded ? '4px 8px 0 0' : '4px 8px 8px 4px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{expanded ? '\u25BC' : '\u25B6'}</span>
          <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{label}</span>
          {materialType && thicknessDisplay && (
            <span style={{
              fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px',
              backgroundColor: color + '15', color, fontWeight: 600,
            }}>
              {materialType} {thicknessDisplay}
            </span>
          )}
          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
            {parts.length} part{parts.length !== 1 ? 's' : ''}
          </span>
          {!expanded && parts.slice(0, 5).map(p => p.svg_data && (
            <div key={p.part_id} style={{
              width: 24, height: 24, overflow: 'hidden', opacity: 0.5,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
              dangerouslySetInnerHTML={{ __html: p.svg_data }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 600 }}>unsaved</span>}
          <button className="btn btn-sm btn-outline-danger"
            style={{ padding: '1px 6px', fontSize: '0.7rem', lineHeight: 1.2 }}
            onClick={e => { e.stopPropagation(); onDelete() }}>&times;</button>
        </div>
      </div>

      {/* Parts grid — always visible when group has parts */}
      {parts.length > 0 && (
        <div style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #f1f5f9' }}>
          <div style={partsGridStyle}>
            {parts.map(part => (
              <PartCard key={part.part_id} part={part}
                materialGroups={allGroups}
                sheetWidth={parseFloat(sheetWidth) || 0}
                sheetLength={parseFloat(sheetLength) || 0}
                edgeMargin={parseFloat(edgeMargin) || 0}
                showGroupDropdown={allGroups.length > 1}
                showColoredBorder={false}
                fillResolved={fillResolutions ? fillResolutions[part.part_id] : undefined}
                onUpdateField={onUpdatePartField}
                onUpdateQuantity={onUpdatePartQuantity}
                onDelete={onDeletePart}
              />
            ))}
          </div>
        </div>
      )}

      {/* Settings form — toggled by expand/collapse */}
      {expanded && (
        <div style={{ padding: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
          <div style={f.row}>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Material Type</label>
              <select className="form-select form-select-sm" value={materialType}
                onChange={e => { setMaterialType(e.target.value); setThicknessDisplay(''); setThicknessIn(''); markDirty() }}>
                <option value="">Select material...</option>
                {[...new Set(materialTypes.map(m => m.type_name || m.material_type))].sort().map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Thickness</label>
              <select className="form-select form-select-sm" value={thicknessDisplay}
                onChange={e => {
                  const sel = filteredThicknesses.find(t => t.display_thickness === e.target.value)
                  handleThicknessChange(e.target.value, sel?.thickness_in?.toString() || '')
                }}>
                <option value="">Select thickness...</option>
                {filteredThicknesses.map(t => (
                  <option key={t.thickness_id} value={t.display_thickness}>
                    {t.display_thickness} ({t.thickness_in}")
                  </option>
                ))}
              </select>
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Gas Type</label>
              <input className="form-control form-control-sm" value={gasType}
                onChange={e => { setGasType(e.target.value); markDirty() }} placeholder="e.g., Nitrogen" />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Surface Finish</label>
              <select className="form-select form-select-sm" value={surfaceFinish}
                onChange={e => { setSurfaceFinish(e.target.value); markDirty() }}>
                <option value="">Select finish...</option>
                {(surfaceFinishes || []).map(sf => (
                  <option key={sf} value={sf}>{sf}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={f.row}>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Sheet Width (in)</label>
              <input type="number" className="form-control form-control-sm" value={sheetWidth}
                onChange={e => { setSheetWidth(e.target.value); markDirty() }} />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Sheet Length (in)</label>
              <input type="number" className="form-control form-control-sm" value={sheetLength}
                onChange={e => { setSheetLength(e.target.value); markDirty() }} />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Feed Rate (IPM)</label>
              <input type="number" className="form-control form-control-sm" value={feedrateIpm}
                onChange={e => { setFeedrateIpm(e.target.value); markDirty() }}
                style={{ backgroundColor: feedrateIpm ? '#d1fae5' : '#fef3c7' }} />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Pierce Time (sec)</label>
              <input type="number" className="form-control form-control-sm" value={pierceTimeSec}
                onChange={e => { setPierceTimeSec(e.target.value); markDirty() }}
                style={{ backgroundColor: pierceTimeSec ? '#d1fae5' : '#fef3c7' }} />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Nozzle Clean (pierces)</label>
              <input type="number" className="form-control form-control-sm" value={nozzleCleanPierces}
                onChange={e => { setNozzleCleanPierces(e.target.value); markDirty() }}
                placeholder="e.g. 500" />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Clean Time (sec)</label>
              <input type="number" className="form-control form-control-sm" value={nozzleCleanSec}
                onChange={e => { setNozzleCleanSec(e.target.value); markDirty() }}
                placeholder="e.g. 60" />
            </div>
          </div>

          <div style={f.row}>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Part Gap (in)</label>
              <input type="number" step="0.0625" className="form-control form-control-sm" value={partGap}
                onChange={e => { setPartGap(e.target.value); markDirty() }} placeholder="Auto from thickness" />
            </div>
            <div style={f.grp}>
              <label className="form-label form-label-sm">Edge Margin (in)</label>
              <input type="number" step="0.0625" className="form-control form-control-sm" value={edgeMargin}
                onChange={e => { setEdgeMargin(e.target.value); markDirty() }} placeholder="Auto from thickness" />
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Cost Inputs</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', cursor: 'pointer', margin: 0 }}>
                <input type="checkbox" checked={customerSupplied}
                  onChange={e => { setCustomerSupplied(e.target.checked); if (e.target.checked) { setCostPerSheet(''); setCostPerLb('') }; markDirty() }} />
                Customer Supplied
              </label>
            </div>
            <div style={f.row}>
              <div style={f.grp}>
                <label className="form-label form-label-sm">$/Sheet</label>
                <input type="number" step="0.01" className="form-control form-control-sm" value={costPerSheet}
                  onChange={e => { setCostPerSheet(e.target.value); markDirty() }} disabled={customerSupplied}
                  style={customerSupplied ? { backgroundColor: '#f1f5f9', color: '#94a3b8' } : costPerSheet ? { backgroundColor: '#d1fae5' } : {}} />
              </div>
              <div style={f.grp}>
                <label className="form-label form-label-sm">$/lb</label>
                <input type="number" step="0.01" className="form-control form-control-sm" value={costPerLb}
                  onChange={e => { setCostPerLb(e.target.value); markDirty() }} disabled={customerSupplied}
                  style={customerSupplied ? { backgroundColor: '#f1f5f9', color: '#94a3b8' } : costPerLb ? { backgroundColor: '#d1fae5' } : {}} />
              </div>
              <div style={f.grp}>
                <label className="form-label form-label-sm">Gas Flow (CFH)</label>
                <input type="number" className="form-control form-control-sm" value={gasFlowCfh}
                  onChange={e => { setGasFlowCfh(e.target.value); markDirty() }}
                  style={gasFlowCfh ? { backgroundColor: '#d1fae5' } : {}} />
              </div>
              <div style={f.grp}>
                <label className="form-label form-label-sm">Gas $/CF</label>
                <input type="number" step="0.01" className="form-control form-control-sm" value={gasCostPerCf}
                  onChange={e => { setGasCostPerCf(e.target.value); markDirty() }}
                  style={gasCostPerCf ? { backgroundColor: '#d1fae5' } : {}} />
              </div>
              <div style={f.grp}>
                <label className="form-label form-label-sm">Consumable $/hr</label>
                <input type="number" step="0.01" className="form-control form-control-sm" value={consumableRate}
                  onChange={e => { setConsumableRate(e.target.value); markDirty() }}
                  style={consumableRate ? { backgroundColor: '#d1fae5' } : {}} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={!dirty}>
              Save Group
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const f = {
  row: { display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  grp: { flex: 1, minWidth: '120px' },
}
